
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  CircularProgress,
  Collapse,
  IconButton,
  Divider,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  TablePagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useDispatch } from "react-redux";
import { setServerStatuses as setServerStatusesAction } from "../redux/slices/serverStatusSlice";

// Monitoring itself now runs on the backend (ServerMonitorService), on a
// fixed interval, independent of whether anyone has this page open. This
// component's only job is to poll the cached results and render them.
const POLL_INTERVAL_MS = 15000;
const STATUS_ENDPOINT = `${process.env.REACT_APP_SUPERADMIN_APP_API_BASE_URL}/super-admin/server-status`;

const getDisplayUrl = (url) => {
  try {
    const parsed = new URL(url);
    const innerUrl = parsed.searchParams.get("url");
    return innerUrl || url;
  } catch {
    return url;
  }
};

function ServiceRow({ service }) {
  return (
    <TableRow sx={{ backgroundColor: "#fafafa" }}>
      <TableCell sx={{ py: 1, width: "5%" }} />
      <TableCell sx={{ py: 1, width: "35%", fontFamily: "Outfit", fontSize: 12, color: "#6b7280" }}>
        {service.id}
      </TableCell>
      <TableCell sx={{ py: 1, width: "15%" }} />
      <TableCell sx={{ py: 1, width: "15%" }} />
      <TableCell sx={{ py: 1, width: "15%" }} />
      <TableCell sx={{ py: 1, width: "15%" }}>
        <Chip
          label={service.status === "up" ? "Up" : "Down"}
          size="small"
          sx={{
            fontFamily: "Outfit",
            fontWeight: 500,
            fontSize: 11,
            height: 20,
            backgroundColor: service.status === "up" ? "#d1fae5" : "#fee2e2",
            color: service.status === "up" ? "#065f46" : "#991b1b",
          }}
        />
      </TableCell>
    </TableRow>
  );
}

// Small badge for the failure category from the classification chart -
// only shown when the server is Inactive.
function FailureCategoryChip({ category }) {
  if (!category || category === "healthy") return null;
  const styles = {
    app_config_issue: { bg: "#fef3c7", color: "#92400e", label: "Config Issue" },
    application_failure: { bg: "#fee2e2", color: "#991b1b", label: "App Failure" },
    infrastructure_failure: { bg: "#fecaca", color: "#7f1d1d", label: "Infra Failure" },
    unknown: { bg: "#e5e7eb", color: "#374151", label: "Unknown" },
  };
  const s = styles[category] || styles.unknown;
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{ ml: 0.5, fontFamily: "Outfit", fontSize: 10, height: 18, backgroundColor: s.bg, color: s.color }}
    />
  );
}

function ServerRow({ server, index }) {
  const [expanded, setExpanded] = useState(false);
  const hasServices = server.type === "rp" && server.services?.length > 0;

  return (
    <>
      <TableRow sx={{ "&:hover": { backgroundColor: "#f9fafb" } }}>
        <TableCell sx={{ width: "5%", fontFamily: "Outfit", color: "#6b7280" }}>{index}</TableCell>
        <TableCell sx={{ width: "35%", fontFamily: "Outfit", fontWeight: 500, color: "#374151" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {server.name}
            {hasServices && (
              <>
                <Typography component="span" sx={{ fontFamily: "Outfit", fontSize: 11, color: "#9ca3af" }}>
                  ({server.services.length} services)
                </Typography>
                <IconButton size="small" onClick={() => setExpanded((p) => !p)} sx={{ p: 0.2 }}>
                  {expanded ? (
                    <KeyboardArrowUpIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                  ) : (
                    <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                  )}
                </IconButton>
              </>
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ width: "15%", fontFamily: "Outfit", color: "#6b7280" }}>{server.category}</TableCell>
        <TableCell sx={{ width: "15%", fontFamily: "Outfit", color: "#6b7280" }}>{server.message ?? "-"}</TableCell>
        <TableCell sx={{ width: "15%", fontFamily: "Outfit", color: "#6b7280" }}>
          {server.lastChecked
            ? new Date(server.lastChecked).toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
              })
            : "-"}
        </TableCell>
        <TableCell sx={{ width: "15%" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Chip
              label={server.status}
              size="small"
              sx={{
                fontFamily: "Outfit",
                fontWeight: 500,
                backgroundColor: server.status === "Active" ? "#d1fae5" : "#fee2e2",
                color: server.status === "Active" ? "#065f46" : "#991b1b",
              }}
            />
            <FailureCategoryChip category={server.failureCategory} />
          </Box>
        </TableCell>
      </TableRow>

      {hasServices && (
        <TableRow>
          <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f3f4f6" }}>
                    <TableCell sx={{ py: 0.5, width: "5%" }} />
                    <TableCell sx={{ py: 0.5, width: "35%", fontFamily: "Outfit", fontSize: 11, fontWeight: 600, color: "#9ca3af" }}>
                      Service
                    </TableCell>
                    <TableCell sx={{ py: 0.5, width: "15%" }} />
                    <TableCell sx={{ py: 0.5, width: "15%" }} />
                    <TableCell sx={{ py: 0.5, width: "15%" }} />
                    <TableCell sx={{ py: 0.5, width: "15%", fontFamily: "Outfit", fontSize: 11, fontWeight: 600, color: "#9ca3af" }}>
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {server.services.map((svc) => (
                    <ServiceRow key={svc.id} service={svc} />
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function ServerStatusTable({ sidebarOpen = true }) {
  const sidebarWidth = sidebarOpen ? 200 : 100;
  const [activeTab, setActiveTab] = useState(0); // 0 = Production, 1 = Testing
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const [serverStatuses, setServerStatuses] = useState([]);

  const fetchStatuses = useCallback(async () => {
    try {
      const res = await fetch(STATUS_ENDPOINT);
      const data = await res.json();
      setServerStatuses(data);
      dispatch(setServerStatusesAction(data));
    } catch (err) {
      console.error("Failed to fetch server statuses:", err);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchStatuses();
    const intervalId = setInterval(fetchStatuses, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [fetchStatuses]);

  const filteredServers = serverStatuses.filter((server) => {
    const matchesRegion =
      activeTab === 0 ? server.region === "Production Server" : server.region === "Testing Server";
    if (!matchesRegion) return false;
    if (!searchQuery.trim()) return true;
    const query = searchQuery.trim().toLowerCase();
    return server.name.toLowerCase().includes(query) || server.category.toLowerCase().includes(query);
  });

  useEffect(() => {
    setPage(0);
  }, [activeTab, searchQuery]);

  const paginatedServers = filteredServers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4, xl: 4 },
        ml: { xs: "0px", sm: "0px", md: `${sidebarWidth}px`, xl: `${sidebarWidth}px` },
        transition: "margin-left 0.3s ease",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, mt: "1.2%" }}>
        <Typography variant="h6" sx={{ fontFamily: "Outfit", fontWeight: 600, color: "#374151" }}>
          Server Status
        </Typography>
        <Typography variant="caption" sx={{ fontFamily: "Outfit", color: "#9ca3af" }}>
          Monitored server-side, refreshed every 15 seconds
        </Typography>
      </Box>
      <Divider sx={{ mb: "2%" }} />

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            "& .MuiTab-root": { fontFamily: "Outfit", fontWeight: 500, fontSize: 13, textTransform: "none", color: "#6b7280", minHeight: 36 },
            "& .Mui-selected": { color: "#9048FB !important", fontWeight: 600 },
            "& .MuiTabs-indicator": { backgroundColor: "#9048FB" },
          }}
        >
          <Tab label="Production" />
          <Tab label="Testing" />
        </Tabs>

        <TextField
          size="small"
          placeholder="Search server by name or category..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            width: { xs: "100%", sm: 300 },
            "& .MuiOutlinedInput-root": {
              fontFamily: "Outfit",
              fontSize: 13,
              backgroundColor: "#fff",
              "& fieldset": { borderColor: "#e5e7eb" },
              "&:hover fieldset": { borderColor: "#9048FB" },
              "&.Mui-focused fieldset": { borderColor: "#9048FB" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, maxHeight: 700, overflow: "auto" }}>
        <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "5%", fontFamily: "Outfit", fontWeight: 600, color: "#374151", backgroundColor: "#f9fafb" }}>#</TableCell>
              <TableCell sx={{ width: "35%", fontFamily: "Outfit", fontWeight: 600, color: "#374151", backgroundColor: "#f9fafb" }}>Server Name</TableCell>
              <TableCell sx={{ width: "15%", fontFamily: "Outfit", fontWeight: 600, color: "#374151", backgroundColor: "#f9fafb" }}>Category</TableCell>
              <TableCell sx={{ width: "15%", fontFamily: "Outfit", fontWeight: 600, color: "#374151", backgroundColor: "#f9fafb" }}>Message</TableCell>
              <TableCell sx={{ width: "15%", fontFamily: "Outfit", fontWeight: 600, color: "#374151", backgroundColor: "#f9fafb" }}>Last Checked</TableCell>
              <TableCell sx={{ width: "15%", fontFamily: "Outfit", fontWeight: 600, color: "#374151", backgroundColor: "#f9fafb" }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={22} sx={{ color: "#9048FB" }} />
                </TableCell>
              </TableRow>
            ) : paginatedServers.length > 0 ? (
              paginatedServers.map((server, index) => (
                <ServerRow key={server.id} server={server} index={page * rowsPerPage + index + 1} />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 4, fontFamily: "Outfit", color: "#9ca3af", fontSize: 13 }}>
                  No servers found matching "{searchQuery}"
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredServers.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          borderBottom: "none",
          "& .MuiTablePagination-toolbar": { fontFamily: "Outfit", color: "#374151" },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontFamily: "Outfit", fontSize: 13, color: "#6b7280" },
        }}
      />
    </Box>
  );
}